import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  adminLogin,
  adminMe,
  adminStats,
  adminListLeads,
  adminListPayments,
  adminToken,
} from "../lib/api";
import {
  SignOut,
  ChartLine,
  Users,
  CreditCard,
  CurrencyInr,
  LockKey,
  Copy,
} from "@phosphor-icons/react";
import Logo from "../components/Logo";
import { toast } from "sonner";

function LoginCard({ onSuccess }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { token } = await adminLogin(username, password);
      adminToken.set(token);
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F3ED] flex items-center justify-center px-5">
      <div className="w-full max-w-md border border-[#0A0A0A] p-8 lg:p-10 bg-[#F4F3ED]">
        <div className="flex items-center gap-3 mb-7">
          <Logo className="h-10 w-auto" />
          <div>
            <div className="font-display font-black text-base tracking-tight">
              GREAT EAGLE TECH
            </div>
            <div className="font-mono-acc text-[9px] tracking-[0.3em] text-[#4A4A4A] uppercase">
              Admin Console
            </div>
          </div>
        </div>
        <div className="font-mono-acc text-[10px] tracking-[0.3em] uppercase text-[#D9381E] mb-2">
          Restricted
        </div>
        <h1 className="font-display font-black text-3xl tracking-tighter mb-6 inline-flex items-center gap-2">
          <LockKey size={26} weight="duotone" /> Sign in
        </h1>
        <form onSubmit={onSubmit} className="space-y-4" data-testid="admin-login-form">
          <div>
            <label className="font-mono-acc text-[10px] tracking-[0.25em] uppercase text-[#4A4A4A] block mb-2">
              Username
            </label>
            <input
              data-testid="admin-username"
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-3 bg-[#F4F3ED] border border-[#0A0A0A] focus:outline-none focus:border-[#D9381E] font-body text-sm rounded-none"
            />
          </div>
          <div>
            <label className="font-mono-acc text-[10px] tracking-[0.25em] uppercase text-[#4A4A4A] block mb-2">
              Password
            </label>
            <input
              data-testid="admin-password"
              required
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 bg-[#F4F3ED] border border-[#0A0A0A] focus:outline-none focus:border-[#D9381E] font-body text-sm rounded-none"
            />
          </div>
          <button
            data-testid="admin-login-btn"
            type="submit"
            disabled={loading}
            className="w-full bg-[#0A0A0A] text-[#F4F3ED] py-3.5 font-body text-sm uppercase tracking-[0.22em] hover:bg-[#D9381E] transition-colors disabled:opacity-60"
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>
        <Link
          to="/"
          className="block mt-6 font-mono-acc text-[10px] tracking-[0.25em] uppercase text-[#4A4A4A] hover:text-[#0A0A0A]"
        >
          ← back to site
        </Link>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="border border-[#0A0A0A] bg-[#F4F3ED] p-5">
      <div className="flex items-center justify-between mb-3">
        <Icon size={22} weight="duotone" />
        <span className="font-mono-acc text-[10px] tracking-[0.25em] text-[#4A4A4A] uppercase">
          {label}
        </span>
      </div>
      <div className="font-mono-acc font-bold text-2xl lg:text-3xl tabular-nums">
        {value}
      </div>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch (_) {
    return String(iso);
  }
}

function copy(text) {
  navigator.clipboard?.writeText(text);
  toast.success("Copied");
}

export default function Admin() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(!!adminToken.get());
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [payments, setPayments] = useState([]);
  const [tab, setTab] = useState("leads");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, l, p] = await Promise.all([
        adminStats(),
        adminListLeads(),
        adminListPayments(),
      ]);
      setStats(s);
      setLeads(l);
      setPayments(p);
    } catch (err) {
      if (err?.response?.status === 401) {
        adminToken.clear();
        setAuthed(false);
      } else {
        toast.error("Failed to load admin data");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authed) return;
    // Validate token first
    adminMe()
      .then(() => load())
      .catch(() => {
        adminToken.clear();
        setAuthed(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  const logout = () => {
    adminToken.clear();
    setAuthed(false);
    navigate("/admin");
  };

  if (!authed) {
    return <LoginCard onSuccess={() => setAuthed(true)} />;
  }

  return (
    <div className="min-h-screen bg-[#F4F3ED] text-[#0A0A0A]">
      <header className="border-b border-[#D4D0C5] bg-[#F4F3ED] sticky top-0 z-30 backdrop-blur">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <Logo className="h-10 w-auto" />
            <div className="leading-tight">
              <div className="font-display font-black text-sm tracking-tight">
                GREAT EAGLE TECH
              </div>
              <div className="font-mono-acc text-[9px] tracking-[0.25em] text-[#D9381E]">
                ADMIN CONSOLE
              </div>
            </div>
          </Link>
          <button
            data-testid="admin-logout"
            onClick={logout}
            className="inline-flex items-center gap-2 border border-[#0A0A0A] px-3 py-2 font-body text-xs uppercase tracking-[0.18em] hover:bg-[#0A0A0A] hover:text-[#F4F3ED] transition-colors"
          >
            <SignOut size={14} weight="bold" /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-[1320px] mx-auto px-5 lg:px-10 py-8 lg:py-12">
        <h1 className="font-display font-black text-3xl lg:text-5xl tracking-tighter mb-6">
          Dashboard
        </h1>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10" data-testid="admin-stats">
          <Stat icon={Users} label="Leads" value={stats?.total_leads ?? "—"} />
          <Stat icon={ChartLine} label="Paid" value={stats?.paid_leads ?? "—"} />
          <Stat icon={CreditCard} label="Sessions" value={stats?.total_transactions ?? "—"} />
          <Stat
            icon={CurrencyInr}
            label="Revenue"
            value={
              stats?.revenue_inr != null
                ? `₹${Number(stats.revenue_inr).toLocaleString("en-IN")}`
                : "—"
            }
          />
        </div>

        <div className="flex items-center gap-1 mb-4 border-b border-[#0A0A0A]">
          {["leads", "payments"].map((t) => (
            <button
              key={t}
              data-testid={`tab-${t}`}
              onClick={() => setTab(t)}
              className={`px-5 py-3 font-mono-acc text-[11px] tracking-[0.25em] uppercase border-b-2 ${
                tab === t ? "border-[#D9381E] text-[#0A0A0A]" : "border-transparent text-[#4A4A4A]"
              }`}
            >
              {t === "leads" ? `Leads (${leads.length})` : `Payments (${payments.length})`}
            </button>
          ))}
          <button
            onClick={load}
            disabled={loading}
            data-testid="admin-refresh"
            className="ml-auto font-mono-acc text-[10px] tracking-[0.25em] uppercase text-[#D9381E] hover:underline"
          >
            {loading ? "Refreshing…" : "↻ Refresh"}
          </button>
        </div>

        {tab === "leads" && (
          <div className="border border-[#0A0A0A] overflow-x-auto" data-testid="leads-table">
            <table className="w-full text-sm">
              <thead className="bg-[#EAE8DF] border-b border-[#0A0A0A]">
                <tr className="text-left font-mono-acc text-[10px] tracking-[0.25em] uppercase">
                  <th className="px-4 py-3">Created</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Phone</th>
                  <th className="px-4 py-3">Experience</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Message</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr
                    key={l.id}
                    className="border-b border-[#D4D0C5] hover:bg-[#EAE8DF]/50"
                    data-testid={`lead-row-${l.id}`}
                  >
                    <td className="px-4 py-3 font-mono-acc text-xs">{fmtDate(l.created_at)}</td>
                    <td className="px-4 py-3 font-body">{l.name}</td>
                    <td className="px-4 py-3 font-body">
                      <button onClick={() => copy(l.email)} className="hover:text-[#D9381E] inline-flex items-center gap-1.5">
                        {l.email} <Copy size={10} />
                      </button>
                    </td>
                    <td className="px-4 py-3 font-mono-acc text-xs">{l.phone}</td>
                    <td className="px-4 py-3 font-mono-acc text-xs">{l.experience}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`font-mono-acc text-[10px] tracking-[0.2em] uppercase px-2 py-1 ${
                          l.status === "paid"
                            ? "bg-[#D9381E] text-white"
                            : "bg-[#EAE8DF] text-[#0A0A0A] border border-[#D4D0C5]"
                        }`}
                      >
                        {l.status || "new"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[#4A4A4A] text-xs max-w-xs truncate">
                      {l.message || "—"}
                    </td>
                  </tr>
                ))}
                {leads.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center font-mono-acc text-xs text-[#4A4A4A] uppercase tracking-[0.25em]">
                      No leads yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === "payments" && (
          <div className="border border-[#0A0A0A] overflow-x-auto" data-testid="payments-table">
            <table className="w-full text-sm">
              <thead className="bg-[#EAE8DF] border-b border-[#0A0A0A]">
                <tr className="text-left font-mono-acc text-[10px] tracking-[0.25em] uppercase">
                  <th className="px-4 py-3">Created</th>
                  <th className="px-4 py-3">Session</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Amount</th>
                  <th className="px-4 py-3">Payment</th>
                  <th className="px-4 py-3">Stripe Status</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr
                    key={p.session_id}
                    className="border-b border-[#D4D0C5] hover:bg-[#EAE8DF]/50"
                    data-testid={`payment-row-${p.session_id}`}
                  >
                    <td className="px-4 py-3 font-mono-acc text-xs">{fmtDate(p.created_at)}</td>
                    <td className="px-4 py-3 font-mono-acc text-xs truncate max-w-[180px]">
                      <button onClick={() => copy(p.session_id)} className="hover:text-[#D9381E]">
                        {p.session_id?.slice(0, 22)}…
                      </button>
                    </td>
                    <td className="px-4 py-3 font-body">{p.metadata?.email || "—"}</td>
                    <td className="px-4 py-3 font-mono-acc">
                      ₹{Number(p.amount || 0).toLocaleString("en-IN")}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`font-mono-acc text-[10px] tracking-[0.2em] uppercase px-2 py-1 ${
                          p.payment_status === "paid"
                            ? "bg-[#D9381E] text-white"
                            : "bg-[#EAE8DF] text-[#0A0A0A] border border-[#D4D0C5]"
                        }`}
                      >
                        {p.payment_status}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono-acc text-xs">{p.status}</td>
                  </tr>
                ))}
                {payments.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center font-mono-acc text-xs text-[#4A4A4A] uppercase tracking-[0.25em]">
                      No payment sessions yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
